import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Bell, Check } from "lucide-react";

export default function NotificationsBell() {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (err) {
      // Bell polls every 15s — a transient failure must not spam the UI.
      // Log once and stay silent.
      console.warn("NotificationsBell load failed:", err?.response?.status || err?.message);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const unread = items.filter(i => !i.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notif-bell"
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
      >
        <Bell className="w-4 h-4 text-slate-700" />
        {unread > 0 && (
          <span className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white rounded-full text-[10px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute end-0 mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="font-semibold text-sm">{t("notifications")}</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-[11px] text-blue-600 hover:underline font-semibold inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> {t("mark_all_read")}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-sm text-slate-400 p-6 text-center">{t("no_notifications")}</p>
            ) : items.map(n => {
              // Support both old (title/message strings) and new (title_key/message_key + params)
              let title = n.title || "";
              let message = n.message || "";
              if (n.title_key) {
                title = t(n.title_key);
                Object.entries(n.params || {}).forEach(([k, v]) => {
                  title = title.replace(`{${k}}`, v);
                });
              }
              if (n.message_key) {
                message = t(n.message_key);
                const params = { ...(n.params || {}) };
                // Translate status_key if present
                if (params.status_key) params.status = t(params.status_key);
                Object.entries(params).forEach(([k, v]) => {
                  message = message.replace(`{${k}}`, v);
                });
              }
              return (
                <div key={n.id} className={`px-4 py-3 border-b border-slate-100 last:border-0 ${!n.read ? "bg-blue-50/60" : ""}`}>
                  <div className="text-sm font-semibold text-slate-900">{title}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{message}</div>
                  <div className="text-[10px] text-slate-400 mt-1 tabular-nums">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
